<script setup lang="ts">
import DOMPurify from 'isomorphic-dompurify'
import { FileText } from 'lucide-vue-next'
import PdfViewer from '~/components/viewers/PdfViewer.vue'
import DocxViewer from '~/components/viewers/DocxViewer.vue'

// The file-rendering surface of the document viewer, factored out so both the full "File" view and the
// "Split" view render the exact same preview (and both keep agent text-highlighting working). It picks the
// right renderer from the file name and exposes `highlightText` by delegating to the active inner viewer.
const props = defineProps<{
  src: string | null
  fileName?: string | null
  name?: string | null
  hasFile: boolean
  hasHtml: boolean
  htmlContent?: string | null
  /** The item's `/segments` URL, when it is the current stored file (MAR-1097). */
  segmentsSrc?: string | null
}>()

const emit = defineEmits<{ (e: 'text-selected', text: string): void }>()

const pdfViewerRef = ref<InstanceType<typeof PdfViewer> | null>(null)
const docxViewerRef = ref<InstanceType<typeof DocxViewer> | null>(null)

const isPdf = computed(() => props.fileName?.toLowerCase().endsWith('.pdf'))
const isDocx = computed(() => props.fileName?.toLowerCase().endsWith('.docx'))
const isImage = computed(() => {
  const name = props.fileName?.toLowerCase() || ''
  return ['.jpg', '.jpeg', '.png', '.heic', '.webp'].some(ext => name.endsWith(ext))
})
const sanitizedHtmlContent = computed(() => (props.htmlContent ? DOMPurify.sanitize(props.htmlContent) : ''))

// Delegate agent highlight requests to whichever renderer is mounted.
async function highlightText(text: string, page?: number): Promise<boolean> {
  if (pdfViewerRef.value) return pdfViewerRef.value.highlightText(text, page)
  if (docxViewerRef.value) return docxViewerRef.value.highlightText(text)
  return false
}

// Whether a highlight attempt now means "found or not" rather than "too early" (MAR-382): a PDF/DOCX
// renderer that has not mounted or finished loading is not ready; any other preview has nothing to load.
function isReady(): boolean {
  if (props.hasFile && props.src && isPdf.value) return pdfViewerRef.value?.isReady() ?? false
  if (props.hasFile && props.src && isDocx.value) return docxViewerRef.value?.isReady() ?? false
  return true
}

// Highlight a normalized region box `[ymin,xmin,ymax,xmax]` (0-1000) on a page — used by the OCR-review
// split view to point at a doubted line. PDFs delegate to the viewer's per-page overlay; single-image
// scans (page is always 1) paint an overlay rectangle sized as a % of the image and scroll it into view.
const imageWrapRef = ref<HTMLElement | null>(null)
const imageBox = ref<[number, number, number, number] | null>(null)
async function highlightBox(page: number, box: [number, number, number, number] | null): Promise<boolean> {
  if (pdfViewerRef.value) return pdfViewerRef.value.highlightBox(page, box)
  if (isImage.value && box) {
    imageBox.value = box
    await nextTick()
    imageWrapRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return true
  }
  return false
}

// The image overlay rectangle as CSS %, straight from the normalized box (the wrapper shrink-wraps the
// image, so % of the wrapper == % of the image — no letterbox math).
const imageBoxStyle = computed(() => {
  const b = imageBox.value
  if (!b) return null
  const [ymin, xmin, ymax, xmax] = b
  return {
    left: `${xmin / 10}%`,
    top: `${ymin / 10}%`,
    width: `${(xmax - xmin) / 10}%`,
    height: `${(ymax - ymin) / 10}%`,
  }
})

// Go to the `/v1/extract` paragraph `[pN]`: only a `.docx` has them (MAR-1097).
async function scrollToSegment(index: number, fallbackText?: string): Promise<boolean> {
  if (docxViewerRef.value) return docxViewerRef.value.scrollToSegment(index, fallbackText)
  return fallbackText ? highlightText(fallbackText) : false
}

defineExpose({ isReady, highlightText, highlightBox, scrollToSegment })
</script>

<template>
  <!-- `min-w-0`: in a pane's flex row a rendered .docx page (fixed width in points) must not hold this
       wider than its pane; it scrolls inside the viewer instead of painting over the next pane. -->
  <div class="flex-1 min-h-0 min-w-0 flex flex-col">
    <div v-if="hasFile && src && isPdf" class="flex-1 min-h-0">
      <PdfViewer ref="pdfViewerRef" :src="src" @text-selected="emit('text-selected', $event)" />
    </div>
    <div v-else-if="hasFile && src && isDocx" class="flex-1 min-h-0">
      <DocxViewer ref="docxViewerRef" :key="src" :src="src" :segments-src="segmentsSrc ?? undefined" @text-selected="emit('text-selected', $event)" />
    </div>
    <div v-else-if="hasFile && src && isImage" class="flex-1 min-h-0 overflow-auto flex items-start justify-center p-4 bg-muted/30">
      <div ref="imageWrapRef" class="relative inline-block">
        <img :src="src" :alt="name || ''" class="max-w-full max-h-full object-contain rounded block" >
        <div
          v-if="imageBoxStyle"
          class="doc-region-highlight absolute pointer-events-none"
          :style="imageBoxStyle"
        />
      </div>
    </div>
    <div v-else-if="hasFile && src" class="flex-1 min-h-0">
      <iframe :src="src" class="w-full h-full border-0" />
    </div>
    <div v-else-if="hasHtml" class="flex-1 overflow-y-auto p-4">
      <div class="marcus-prose text-sm" v-html="sanitizedHtmlContent" />
    </div>
    <div v-else class="flex-1 flex items-center justify-center px-4">
      <div class="text-center">
        <FileText class="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
        <p class="text-sm text-muted-foreground">No file preview available</p>
        <p class="text-xs text-muted-foreground mt-1">Switch to metadata view for document details</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.doc-region-highlight {
  background-color: color-mix(in srgb, var(--color-warning) 22%, transparent);
  border: 2px solid var(--color-warning-ink);
  border-radius: 2px;
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-warning) 30%, transparent);
}
</style>
